import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { Drawer, Link, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { Action } from 'redux';

import lang from '../../lib/language';
import { setUseBonusAccount as setUseBonusAccountCall } from '../../lib/store/crm';

import HamburgerIcon from '../../assets/img/hamburger.png';
import PoweredByHammerIcon from '../../assets/img/hammer-powered.svg';
import PortalLogo from '../../assets/img/portal_logo_carousel.svg';
import config from '../../config';
import { useBrowserWidth, useIsMobile } from '../../hooks';
import { ClickableImage } from '../index';
import WelcomeSubscriptionPopup from '../WelcomeSubscriptionPopup/WelcomeSubscriptionPopup';

import PopoverComponent from './Popover/PopoverComponent';
import { CAPITAL_MENU_LIST, CAPITAL_MENU_LIST_MOBILE, SUPPORT_LIST } from './Sidebar.constans';
import { SideBarItem } from './Sidebar.types';

import styles from './Sidebar.module.scss';

const MIDDLE_WIDTH = 960;
const LARGE_WIDTH = 1439;

type Props = {
  isCapitalHamburger?: boolean;
} & ReduxProps;

const Sidebar = (props: Props) => {
  const {
    isCapitalHamburger,
  } = props;
  const { browserWidthSize } = useBrowserWidth();

  const location = useLocation();
  const { isMobile } = useIsMobile();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [popoverLabel, setPopoverLabel] = useState<string>('');
  const [popoverStatus, setPopoverStatus] = useState<HTMLDivElement | null>(null);
  const [showBars, setShowBars] = useState<boolean>(false);

  useEffect(() => {
    if (browserWidthSize <= MIDDLE_WIDTH) {
      setIsOpen(false);
      setShowBars(true);
    } else {
      setShowBars(false);
      setIsOpen(true);
    }
  }, [browserWidthSize]);

  const handlePopover = (event, label: string) => {
    setPopoverLabel(label);
    setPopoverStatus(event.currentTarget);
  };

  const isActiveBtn = (item: SideBarItem): boolean => location.pathname === item.path;

  const handleCLickBtn = () => {
    if (browserWidthSize <= MIDDLE_WIDTH) {
      return setIsOpen(false);
    }

    return null;
  };

  const useStyles = makeStyles((theme) => ({
    drawerPaper: {
      background: isCapitalHamburger || showBars ? '#133851' : 'transparent', // checked showBars as well. This is for when the browser is not mobile size but it is tablet size
      border: 'none',
      overflow: 'hidden',
    },
  }));

  const classes = useStyles();

  const isMediumBrowserSize = browserWidthSize > MIDDLE_WIDTH && browserWidthSize <= LARGE_WIDTH;

  const getMenuList = () => {
    if (isCapitalHamburger) {
      return CAPITAL_MENU_LIST_MOBILE;
    }

    return CAPITAL_MENU_LIST;
  };

  return (
    <>
      {showBars
        && (
          <ClickableImage
            src={HamburgerIcon}
            className={isCapitalHamburger ? '' : styles.btnSideBar}
            onClick={() => setIsOpen(!isOpen)}
          />
        )}
      {
        isOpen && (
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            transitionDuration={40}
            variant={browserWidthSize > MIDDLE_WIDTH ? 'persistent' : 'temporary'}
            open={isOpen}
            onClose={() => setIsOpen(false)}
          >
            <div className={classNames(styles.wrapperSidebar,
              { [styles.stopMargin]: isCapitalHamburger })}
            >
              <div className={classNames(styles.logoWrapper,
                { [styles.hamburgerLogo]: isCapitalHamburger })}
              >
                <img src={PortalLogo} className={styles.portalLogo} alt="logo" />
              </div>
              {!isCapitalHamburger && (
                <div className={styles.accountButtonsAndTitle}>
                  <WelcomeSubscriptionPopup />
                </div>
              )}

              <div className={styles.wrapperButton}>
                {
                  getMenuList().map((item: SideBarItem) => (
                    <NavLink
                      id={item.id}
                      key={`${item.id}-${item.path}`}
                      to={{
                        pathname: item.path,
                      }}
                      state={{
                        isClickedBtnSidebar: true,
                      }}
                      aria-owns={popoverStatus ? 'mouse-over-popover' : undefined}
                      aria-haspopup="true"
                      onMouseEnter={(event) => handlePopover(event, item.label)}
                      onMouseLeave={() => setPopoverStatus(null)}
                      onClick={handleCLickBtn}
                    >
                      {
                        (isMediumBrowserSize)
                          ? <div className={isActiveBtn(item) ? styles.activeImage : styles.image}>{item.img}</div>
                          : (
                            <div className={styles.button}>
                              <div className={isActiveBtn(item) ? styles.activeBtn : styles.btn}>
                                <div className={styles.imageAndLabel}>{item.img}</div>
                                <span>{item.label}</span>
                              </div>
                            </div>
                          )
                      }
                    </NavLink>
                  ))
                }
              </div>
              <div className={isMediumBrowserSize
                ? styles.shortDivider
                : styles.longDivider}
              />
              <div className={[styles.wrapperButton, styles.helperAndPortalButtons].join(' ')}>
                {
                  SUPPORT_LIST.map((item: SideBarItem) => (
                    <Link
                      href="mailto:support-pari@capital.bg"
                      key={`${item.id}-${item.label}`}
                      target="_blank"
                      className={location.pathname === item.path ? styles.activeButton : styles.button}
                      aria-owns={popoverStatus ? 'mouse-over-popover' : undefined}
                      aria-haspopup="true"
                      onMouseEnter={(event) => handlePopover(event, item.label)}
                      onMouseLeave={() => setPopoverStatus(null)}
                    >
                      {
                        (isMediumBrowserSize)
                          ? (
                            <div className={styles.image}>{item.img}</div>
                          ) : (
                            <div className={styles.button}>
                              <div className={styles.btn}>
                                <div className={styles.imageAndLabel}>{item.img}</div>
                                {lang.commonContactUs()}
                              </div>
                            </div>
                          )
                      }
                    </Link>
                  ))
                }
                {
                  (browserWidthSize > MIDDLE_WIDTH && browserWidthSize < LARGE_WIDTH) && (
                    <PopoverComponent
                      open={Boolean(popoverStatus)}
                      popoverStatus={popoverStatus}
                      handlePopoverClose={() => setPopoverStatus(null)}
                      label={popoverLabel}
                    />
                  )
                }
              </div>
              {!isMediumBrowserSize && (
              <div className={styles.poweredBy}>
                <a href={config.links.alaricUrl} target="_blank" rel="noopener noreferrer">
                  <img
                    src={PoweredByHammerIcon}
                    className={styles.poweredByLogo}
                    alt="Powered by Alaric Securities"
                  />
                </a>
              </div>
              )}
            </div>
          </Drawer>
        )
      }
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  setUseBonusAccount: (value: boolean) => dispatch(setUseBonusAccountCall(value)),
});

const connector = connect(null, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Sidebar);
